import { useCallback, useMemo, useRef, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import { PagesUploadFilesBulkDeleteDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'

import { DiscardChangesDialog } from './components/DiscardChangesDialog'
import { Preview, type PreviewHandle } from './components/Preview'
import { type ImportedPage, Upload, type UploadHandle } from './components/Upload'
import { UploadingSkeleton } from './components/UploadingSkeleton'

interface UploadFilesModalProps {
  isOpen: boolean
  onClose: () => void
  folderType?: string
}

type PreviewState = {
  isLastPage: boolean
  canProceed: boolean
  isPublishing: boolean
}

export const UploadFilesModal = ({ isOpen, onClose, folderType }: UploadFilesModalProps) => {
  const [importedPages, setImportedPages] = useState<ImportedPage[]>([])
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [uploadCanProceed, setUploadCanProceed] = useState(false)
  const [previewState, setPreviewState] = useState<PreviewState>({
    isLastPage: false,
    canProceed: false,
    isPublishing: false,
  })

  const uploadRef = useRef<UploadHandle>(null)
  const previewRef = useRef<PreviewHandle>(null)

  const [bulkDeletePages] = useMutation(PagesUploadFilesBulkDeleteDocument, {
    extractorFn: (data) => data.handbookPagesMutations.bulkDeleteHandbookpagesPage,
  })

  const uploadState = useMemo(() => {
    if (isImporting) return 'importing'
    return importedPages.length > 0 ? 'preview' : 'upload'
  }, [importedPages.length, isImporting])

  const handleCloseAttempt = () => {
    // Prevent closing while loading
    if (isImporting) return

    if (uploadState === 'preview') {
      setShowConfirmDialog(true)
    } else {
      handleConfirmClose()
    }
  }

  const handleConfirmClose = async () => {
    // If there are imported pages, delete them before closing
    if (importedPages.length > 0) {
      const pageIds = importedPages.map((page) => page.id)
      await bulkDeletePages({ variables: { ids: pageIds } })
    }

    setImportedPages([])
    setShowConfirmDialog(false)
    onClose()
  }

  const handleNext = (pages: ImportedPage[]) => {
    setImportedPages(pages)
  }

  const onPublish = () => {
    setImportedPages([])
    onClose()
  }

  const modalPosition = useMemo(() => {
    if (uploadState === 'preview' || uploadState === 'importing') return 'fullscreen'
    return 'center'
  }, [uploadState])

  const getModalTitle = () => {
    if (uploadState === 'preview') return i18n.t('pages.upload_files_modal.preview.title')
    if (uploadState === 'upload') return i18n.t('pages.upload_files_modal.upload.title')
    return ''
  }

  const handleUploadCanProceedChange = useCallback((canProceed: boolean) => {
    setUploadCanProceed(canProceed)
  }, [])

  const handlePreviewStateChange = useCallback((state: PreviewState) => {
    setPreviewState(state)
  }, [])

  const getPrimaryAction = () => {
    if (uploadState === 'importing') {
      return undefined
    }

    if (uploadState === 'upload') {
      return {
        label: i18n.t('common.next'),
        onClick: () => uploadRef.current?.handleNext(),
        disabled: !uploadCanProceed || isImporting,
        isLoading: isImporting,
      }
    }

    if (uploadState === 'preview') {
      const { isLastPage, canProceed, isPublishing } = previewState
      return {
        label: isLastPage ? i18n.t('common.publish') : i18n.t('common.next'),
        onClick: () => previewRef.current?.handleAction(),
        disabled: !canProceed || isPublishing,
        isLoading: isPublishing,
      }
    }

    return undefined
  }

  return (
    <>
      <F0Dialog
        disableContentPadding
        isOpen={!!isOpen}
        onClose={() => {
          if (!isImporting) {
            handleCloseAttempt()
          }
        }}
        position={modalPosition}
        title={getModalTitle()}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: handleCloseAttempt,
        }}
        primaryAction={getPrimaryAction()}
      >
        {uploadState === 'importing' ? (
          <UploadingSkeleton />
        ) : uploadState === 'preview' ? (
          <Preview
            ref={previewRef}
            onCloseDirectly={handleConfirmClose}
            onPublish={onPublish}
            importedPages={importedPages}
            folderType={folderType}
            onStateChange={handlePreviewStateChange}
          />
        ) : (
          <Upload
            ref={uploadRef}
            setIsImporting={setIsImporting}
            isImporting={isImporting}
            onNext={handleNext}
            folderType={folderType}
            onCanProceedChange={handleUploadCanProceedChange}
          />
        )}
      </F0Dialog>

      <DiscardChangesDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </>
  )
}
